import React, { useState, useEffect } from 'react';
import { Modal, Button, Checkbox, Toast } from '@douyinfe/semi-ui';
import { IconAlertTriangle, IconTickCircle } from '@douyinfe/semi-icons';
import { API_USER } from '../helpers';

// 协议正文里的 **xxx** 渲染成加粗(忠实原文的重点)
function renderClause(text) {
  const parts = String(text || '').split('**');
  return parts.map((p, i) => (i % 2 === 1 ? <strong key={i}>{p}</strong> : <span key={i}>{p}</span>));
}

// 全局协议弹窗的事件名
const OPEN_EVENT = 'meow:agreement-open';
let _resolver = null;

// 打开全局协议弹窗,返回 Promise<boolean>:
//   true  = 用户勾选并签署成功
//   false = 用户取消/关闭(未签署)
// 由 AgreementGate 组件监听事件后实际渲染弹窗。
export function openAgreementDialog({ doc, apiKey }) {
  return new Promise((resolve) => {
    _resolver = resolve;
    window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { doc, apiKey } }));
  });
}

// 协议弹窗(首页查余额时自动弹出;这是唯一的签署入口)
//
// 布局(自上而下四段,别改顺序):
//   ① 标题栏  —— 图标 + 标题 + 版本标签
//   ② 说明条  —— 浅灰底,一句话说明这是首次使用须知
//   ③ 条款区  —— 唯一可滚动的一段,每条独立成行、带序号徽章
//   ④ 同意区  —— **固定不滚动**,勾选框永远可见(用户不用滚到底才找得到)
//   底部 footer:左侧橙色警示语 + 右侧两个按钮
//
// ⚠️ 三条硬要求(别改):
//   1. **默认不勾选** —— 勾选框初始必须是未选中状态。
//   2. **必须手动勾选** —— 没勾选时「激活服务」是禁用的,点不动。
//      (勾选框的文字也必须放进 children,让点文字能勾中)
//   3. **签署动作不计费** —— 提交的是独立的签署接口,不产生调用日志。
const AgreementModal = ({ visible, doc, apiKey, onDone, onCancel }) => {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [agreeError, setAgreeError] = useState('');

  // 每次打开都重置为"未勾选"
  useEffect(() => {
    if (visible) {
      setChecked(false);
      setSubmitting(false);
      setAgreeError('');
    }
  }, [visible]);

  if (!doc) return null;

  const submit = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    setAgreeError('');
    try {
      const r = await API_USER.post('/api/agreement/sign', {
        version: doc.version,
        agreed: true,
      }, {
        headers: { Authorization: `Bearer ${apiKey || ''}` },
      });
      const d = r.data?.data || {};
      Toast.success('已签署,现在可以正常使用本服务');
      onDone && onDone(d);
    } catch (e) {
      const msg = e.response?.data?.message || '签署失败,请稍后重试';
      setAgreeError(msg);
      Toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = () => {
    onCancel && onCancel();
  };

  return (
    <Modal
      visible={visible}
      onCancel={cancel}
      width={640}
      className="agreement-modal"
      bodyStyle={{ padding: 0 }}
      title={doc.title || '服务使用知情确认书'}
      footer={
        <div className="agreement-modal__footer">
          {/* ⚠️ 底部警示行:不签就不能用 —— 文字前必须有警示小图标。 */}
          <div className="agreement-modal__warn">
            <IconAlertTriangle className="agreement-modal__warn-icon" />
            <span>{doc.footer}</span>
          </div>
          <div className="agreement-modal__actions">
            <Button onClick={cancel}>暂不使用</Button>
            {/* 未签署时点「查询余额」会走到这里 —— 勾选同意即等于把服务激活,
                所以主按钮叫「激活服务」而不是「同意并继续」。 */}
            <Button type="primary" theme="solid" onClick={submit} disabled={!checked} loading={submitting}>
              激活服务
            </Button>
          </div>
        </div>
      }
    >
      <div className="agreement-modal__body">
        {/* ② 说明条 */}
        <div className="agreement-modal__lead">
          <IconTickCircle className="agreement-modal__lead-icon" />
          <div className="agreement-modal__lead-text">
            <div className="agreement-modal__lead-title">首次使用须知</div>
            <div className="agreement-modal__lead-desc">{doc.intro}</div>
          </div>
        </div>

        {/* ③ 条款区(唯一可滚动的一段) */}
        <div className="agreement-modal__clauses">
          {(doc.clauses || []).map((c, i) => (
            <div className="agreement-modal__clause" key={i}>
              <span className="agreement-modal__no">{i + 1}</span>
              <div className="agreement-modal__clause-text">{renderClause(c)}</div>
            </div>
          ))}
        </div>

        {/* ④ 同意区(固定不滚动) */}
        <div className="agreement-modal__consent">
          {/* ⚠️ 两个坑,别改:
              1. 文字必须放在 Checkbox 的 children 里 —— Semi 把 children 包进内部的
                 addon 容器,点文字才会触发切换。之前文字是并排的 <span>,点它完全没反应。
              2. Semi 的 Checkbox onChange 传的是**事件对象**不是布尔值
                 (onChange(e),新状态在 e.target.checked);
                 直接 onChange={setChecked} 会把事件对象塞进 checked,
                 而 Semi 内部只在 isBoolean(props.checked) 时才同步状态 →
                 勾选框永远保持未选中,按钮永远点不动。 */}
          <Checkbox checked={checked} onChange={(e) => setChecked(!!(e && e.target && e.target.checked))}>
            <span className="agreement-modal__consent-text">{doc.checkbox}</span>
          </Checkbox>
          {agreeError ? <div className="agreement-modal__error">{agreeError}</div> : null}
        </div>
      </div>
    </Modal>
  );
};

export default AgreementModal;

// AgreementGate:全局协议弹窗宿主。
// 挂在 App 最外层,任何地方调用 openAgreementDialog() 都会弹这套协议。
export const AgreementGate = () => {
  const [state, setState] = useState(null); // { doc, apiKey }

  useEffect(() => {
    const onOpen = (e) => {
      setState(e.detail || null);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  const done = () => {
    if (_resolver) { _resolver(true); _resolver = null; }
    setState(null);
  };
  const cancel = () => {
    if (_resolver) { _resolver(false); _resolver = null; }
    setState(null);
  };

  return (
    <AgreementModal
      visible={!!state}
      doc={state?.doc}
      apiKey={state?.apiKey}
      onDone={done}
      onCancel={cancel}
    />
  );
};
